import TextButton from 'LuxKit/Button/TextButton'
import Group from 'components/utils/Group'
import type { ComponentProps, ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'
import { useAppSelector } from 'hooks/reduxHooks'
import useSwitchRegion from 'hooks/useSwitchRegion'
import useLuxPlusLandingActions from 'luxPlus/hooks/useLuxPlusLandingActions'
import { getCanRedeemLuxPlusBenefits, getHasLuxPlusInAnyRegion, getIsFreePreviewLuxPlusMember, getIsFreePreviewMemberOutsideRegion, getIsLuxPlusMemberOutsideRegion } from 'luxPlus/selectors/featureToggle'
import LuxPlusLogo from 'luxPlus/components/Logos/LuxPlusLogo'
import styled from 'styled-components'
import clsx from 'clsx'
import { rem } from 'polished'

const InlineLogoWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  margin: 0 ${rem(4)};
  vertical-align: -0.04em;

  &.colour-brand {
    color: ${props => props.theme.palette.product.luxPlus.background};
  }

  &.colour-inherit {
    color: currentColor;
  }
`

const InlineLuxPlusLogo = styled(LuxPlusLogo)`
  display: block;
`

interface Props {
  becomeMemberLabel: string
  startFreePreviewLabel: string
  becomeMemberDisabled?: boolean
  className?: string
  fit?: ComponentProps<typeof TextButton>['fit']
  gap?: ComponentProps<typeof Group>['gap']
  desktopHorizontalAlign?: ComponentProps<typeof Group>['desktopHorizontalAlign']
  horizontalAlign?: ComponentProps<typeof Group>['horizontalAlign']
  includeSpoofNoJoinFee?: boolean
  normalFooter?: ReactNode
  spoofNoJoinFeeLabel?: string
  switchRegionLogoColour?: 'brand' | 'inherit'
  tabletDirection?: ComponentProps<typeof Group>['tabletDirection']
  tabletGap?: ComponentProps<typeof Group>['tabletGap']
  tabletHorizontalAlign?: ComponentProps<typeof Group>['tabletHorizontalAlign']
  variant?: ComponentProps<typeof TextButton>['variant']
  wrap?: ComponentProps<typeof Group>['wrap']
}

function LuxPlusLandingCtaButtons({
  becomeMemberLabel,
  startFreePreviewLabel,
  becomeMemberDisabled,
  className,
  fit,
  gap = 8,
  desktopHorizontalAlign,
  horizontalAlign,
  includeSpoofNoJoinFee = false,
  normalFooter,
  spoofNoJoinFeeLabel,
  switchRegionLogoColour = 'brand',
  tabletDirection = 'horizontal',
  tabletGap,
  tabletHorizontalAlign,
  variant,
  wrap,
}: Props) {
  const isOutsideMemberRegion = useAppSelector(getIsLuxPlusMemberOutsideRegion)
  const isFreePreviewOutsideRegion = useAppSelector(getIsFreePreviewMemberOutsideRegion)
  const isPreview = useAppSelector(getIsFreePreviewLuxPlusMember)
  const canRedeemBenefits = useAppSelector(getCanRedeemLuxPlusBenefits)
  const hasPaidLuxPlusMembership = useAppSelector(getHasLuxPlusInAnyRegion)
  const memberRegion = useAppSelector(state => state.auth.account.luxPlus.member.subscription.item?.region?.toLowerCase())
  const showMembershipCta = !canRedeemBenefits && !hasPaidLuxPlusMembership
  const [switchRegionPath, handleSwitchRegion] = useSwitchRegion(memberRegion)
  const { handleBecomeMember, handleBecomeMemberNoJoinFee, handleStartFreePreview, isSpoofed } = useLuxPlusLandingActions()
  const groupProps = {
    className,
    direction: 'vertical' as const,
    desktopHorizontalAlign,
    gap,
    horizontalAlign,
    tabletDirection,
    tabletGap,
    tabletHorizontalAlign,
    wrap,
  }

  if (isOutsideMemberRegion || isFreePreviewOutsideRegion) {
    return <Group {...groupProps}>
      <TextButton
        kind="primary"
        variant={variant}
        fit={fit}
        href={switchRegionPath}
        onClick={handleSwitchRegion}
      >
        <FormattedMessage
          id="VTICxidguqZ5x30E0c6_"
          defaultMessage="Switch to your {luxPlusLogo} region"
          description="CTA button to switch to the LuxPlus membership region on the landing page"
          values={{
            luxPlusLogo: <InlineLogoWrapper className={clsx(`colour-${switchRegionLogoColour}`)}><InlineLuxPlusLogo colour="inherit" height={11} /></InlineLogoWrapper>,
          }}
        />
      </TextButton>
    </Group>
  }

  if (!showMembershipCta) return null

  return <>
    <Group {...groupProps}>
      <TextButton kind="primary" variant={variant} fit={fit} disabled={becomeMemberDisabled} onClick={handleBecomeMember}>
        {becomeMemberLabel}
      </TextButton>
      {includeSpoofNoJoinFee && isSpoofed && spoofNoJoinFeeLabel && <TextButton kind="primary" variant={variant} fit={fit} onClick={handleBecomeMemberNoJoinFee}>
        {spoofNoJoinFeeLabel}
      </TextButton>}
      {!isPreview && (
        <TextButton kind="secondary" variant={variant} fit={fit} onClick={handleStartFreePreview}>
          {startFreePreviewLabel}
        </TextButton>
      )}
    </Group>
    {normalFooter}
  </>
}

export default LuxPlusLandingCtaButtons
